import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import classNames from 'classnames';

import { addPublicURL } from '../../Functions/AddPublicURL';
import { getIconPath } from '../../Functions/GetIconPath';
import { getFromLocalStorage, saveToLocalStorage } from '../../Functions/LocalStorageUtils';
import { setSelectedActuator } from '../../redux/reducers/actuatorsRedurer';
import { setActuators, setAppliances, setBottomBarOpen, setFloorplanImage, setShowActuatorDetail } from '../../redux/reducers/appReducer';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import Button from '../Button/Button';

import styles from './BottomBar.module.scss';

export interface BottomBarPropsI {
  children?: React.ReactNode;
  bottomDrawerOpen?: boolean;
}

const BottomBar = ({ children, bottomDrawerOpen }: BottomBarPropsI): JSX.Element => {
  return (
    <>
      <div
        id="tutorial-bottom-menu"
        className={classNames(styles.BottomBar, {
          [styles.BottomBar__open]: bottomDrawerOpen
        })}
      >
        {children}
      </div>
      <span className={styles.BottomBar__version}>BETA v{process.env.REACT_APP_VERSION} (Only Wireless tech)</span>
      <UploadFloorplanIcon />
    </>
  );
};

const Opener: React.FC<{ children: React.ReactNode; bottomDrawerOpen?: boolean }> = ({ children, bottomDrawerOpen }) => {
  const rightMenuShown = useAppSelector((state) => state.app.rightMenuShown);
  if(bottomDrawerOpen) return null;

  return (
    <div className={classNames(styles.BottomBar__opener, {
      [styles.BottomBar__opener__rightMenuShown]: rightMenuShown
    })}>
      {children}
    </div>
  );
}

const DinRail: React.FC = ({}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  return (
    <div className={styles.BottomBar__dinRail}>
      <div className={styles.BottomBar__dinRail__header}>
        <div className={styles.BottomBar__dinRail__header__back}>
          <img src={getIconPath('arrow_back')} alt="Back" onClick={() => dispatch(setBottomBarOpen(false))}/>
          <Button variant='fill' size='md' onClick={() => dispatch(setBottomBarOpen(false))}>{t('electrical_cabinet.apply')}</Button>
        </div>
        <h3>{t('electrical_cabinet.heading')}</h3>
        <p>{t('electrical_cabinet.description')}</p>
      </div>
      <div className={styles.BottomBar__dinRail__content}>
        <div className={styles.BottomBar__dinRail__content__background}>
          <img src={addPublicURL('assets/electrical_cabinet.png')} alt="Electrical Cabinet" />
        </div>
        <div 
            className={styles.BottomBar__dinRail__content__rail}
            style={{
              backgroundImage: `url('${addPublicURL('assets/DIN_gray_seamless.png')}')`,
              backgroundRepeat: 'repeat-x',
              backgroundSize: 'auto 100%',
              backgroundPosition: 'left center',
              zIndex: 1,
            }}
        ></div>
      </div>
    </div>
  );
}

const Actuators: React.FC = ({}) => {
  const actuators = useAppSelector((state) => state.app.actuators);
  const appliances = useAppSelector((state) => state.app.appliances);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const railRef = useRef<HTMLDivElement | null>(null);

  const showActuatorDetail = (actuatorId: number) => {
      const actuator = actuators.find(a => a.id === actuatorId);
      if(!actuator){
          toast.error(t('rightMenu.actuators.error'));
          return;
      }
      dispatch(setSelectedActuator(actuator));
      dispatch(setShowActuatorDetail(true));
  }

  const deleteActuator = (actuatorId: number) => {
    dispatch(setActuators(actuators.filter((actuator) => actuator.id !== actuatorId)));

    dispatch(
      setAppliances(
        appliances.map((appliance) => {
          if (appliance.actuator === actuatorId) {
            const nextAppliance = { ...appliance };
            nextAppliance.actuator = undefined;
            return nextAppliance;
          }
          return appliance;
        })
      )
    );

    dispatch(setShowActuatorDetail(false));
  };

  const handleActuatorDragEnd = (event: React.DragEvent<HTMLImageElement>, actuatorId: number) => {
    const rail = railRef.current;
    if (!rail) {
      return;
    }

    const railRect = rail.getBoundingClientRect();
    const droppedOutsideRail =
      event.clientX < railRect.left ||
      event.clientX > railRect.right ||
      event.clientY < railRect.top ||
      event.clientY > railRect.bottom;

    if (droppedOutsideRail) {
      deleteActuator(actuatorId);
    }
  };

  const handleActuatorRefMouseEnter = (event: React.MouseEvent<HTMLImageElement>) => {
    event.currentTarget.parentElement?.classList.add(styles.BottomBar__actuatorsRef__content__item__hover);
  };

  const handleActuatorRefMouseLeave = (event: React.MouseEvent<HTMLImageElement>) => {
    event.currentTarget.parentElement?.classList.remove(styles.BottomBar__actuatorsRef__content__item__hover);
  };

  return (
    <>
    <div className={styles.BottomBar__actuators}>
      <div ref={railRef} className={styles.BottomBar__actuators__content}>
        {actuators.map((actuator) => {
          if(!actuator.dinrail) return null;
          return (
            <div key={actuator.id} className={styles.BottomBar__actuators__content__item}>
              <img
                draggable
                src={addPublicURL(`assets/actuators/${actuator.refImg}`)}
                alt={actuator.name}
                onDragStart={(event) => {
                  event.dataTransfer.setData('application/dinrail-actuator-id', `${actuator.id ?? ''}`);
                  event.dataTransfer.effectAllowed = 'move';
                }}
                onDragEnd={(event) => handleActuatorDragEnd(event, actuator.id ?? 0)}
                onClick={() => showActuatorDetail(actuator.id ?? 0)}
              />
            </div>
          )
        })}
      </div>
    </div>
    <div className={styles.BottomBar__actuatorsRef}>
      <div ref={railRef} className={styles.BottomBar__actuatorsRef__content}>
        {actuators.map((actuator) => {
          if(!actuator.dinrail) return null;
          return (
            <div key={actuator.id} className={styles.BottomBar__actuatorsRef__content__item}>
              <img
                draggable
                src={addPublicURL(`assets/actuators/${actuator.refImg}`)}
                alt={actuator.name}
                onDragStart={(event) => {
                  event.dataTransfer.setData('application/dinrail-actuator-id', `${actuator.id ?? ''}`);
                  event.dataTransfer.effectAllowed = 'move';
                }}
                onMouseEnter={handleActuatorRefMouseEnter}
                onMouseLeave={handleActuatorRefMouseLeave}
                onDragEnd={(event) => handleActuatorDragEnd(event, actuator.id ?? 0)}
                onClick={() => showActuatorDetail(actuator.id ?? 0)}
              />
            </div>
          )
        })}
      </div>
    </div>
    </>
  );
}

const UploadFloorplanIcon: React.FC = ({}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [hover, setHover] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const leftMenuShown = useAppSelector((state) => state.app.leftMenuShown);

  const handleUploadFloorplanClick = () => {
    fileInputRef.current?.click();
  };

  const handleUploadFloorplanChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      return;
    }

    if (!selectedFile.type.startsWith('image/')) {
      toast.error(t('floorplan.upload.invalidType'));
      event.target.value = '';
      return;
    }

    const maxSizeInBytes = 8 * 1024 * 1024;
    if (selectedFile.size > maxSizeInBytes) {
      toast.error(t('floorplan.upload.tooLarge'));
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        toast.error(t('floorplan.upload.failed'));
        return;
      }

      dispatch(setFloorplanImage(result));
      saveToLocalStorage('custom_floorplan_image', result);
      toast.success(t('floorplan.upload.success'));
    };

    reader.onerror = () => {
      toast.error(t('floorplan.upload.failed'));
    };

    reader.readAsDataURL(selectedFile);
    event.target.value = '';
  };

  const handleResetFloorplan = () => {
    dispatch(setFloorplanImage(null));
    saveToLocalStorage('custom_floorplan_image', '');
    toast.success(t('floorplan.upload.resetSuccess'));
  };

  React.useEffect(() => {
    const storedImage = getFromLocalStorage('custom_floorplan_image');
    if (storedImage) {
      dispatch(setFloorplanImage(storedImage));
    }
  }, [dispatch]);

  return (
    <div
      className={classNames(styles.BottomBar__uploadFloorplanIcon, {
        [styles.BottomBar__uploadFloorplanIcon__leftMenuShown]: leftMenuShown,
      })}
    
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleUploadFloorplanChange}
        style={{ display: 'none' }}
      />
      <div
        className={styles.BottomBar__uploadFloorplanIcon__item}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={handleUploadFloorplanClick}
      >
        <img src={hover ? getIconPath('add_photo') : getIconPath('add_photo_active')} alt="Upload Floorplan"/>
      </div>
      <p style={{ display: hover ? 'block' : 'none' }}>{t('upload_floorplan')}</p>
      <button
        type="button"
        className={styles.BottomBar__uploadFloorplanIcon__reset}
        onClick={handleResetFloorplan}
      >
        {t('floorplan.upload.reset')}
      </button>
    </div>
  )
}

BottomBar.opener = Opener;
BottomBar.dinRail = DinRail;
BottomBar.actuators = Actuators;
BottomBar.UploadFloorplanIcon = UploadFloorplanIcon;
export default BottomBar;
